<template lang="pug">
  DrawerContents
    RogueInput(v-model="name" label="Title")
    Field(label="Share Settings")
      Radio(:option="true" v-model="isPublic" name="makePublic") {{__('Make Room Public')}}
      Radio(:option="false" v-model="isPublic" name="makePublic") {{__('Keep Private')}}
    Field(label="Share Options" v-if="project.isPublic")
      RogueButton(@click="share()") {{__('Share Link')}}
      h5.share-url-title {{__('Or copy the following URL:')}}
      .url {{url}}
      Message(status="success" v-if="notice === 'clipboard'")
        Icon.left-icon(name="misc/share-arrow")
        | {{__('URL has been copied to your clipboard')}}
    .fill
    template(#bottom)
      .bottom
        Message(status="success" v-if="notice === 'saved'")
          | {{__('Your gym has been saved.')}}
        Message(status="error" v-if="notice === 'save-error'")
          Icon.left-icon(name="alert-error")
          | {{__('There was an error saving your gym. Please try again later.')}}
        Message(status="warning" v-if="isPublic !== project.isPublic")
          | {{__('Save your gym for new share settings to take effect.')}}
        RogueButton(
          color="black"
          width="full"
          @click="save()"
          :disabled="!canSave"
          :loading="saving"
        ) {{__('Save')}}
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRogue } from '@/js/services/Rogue'
import type { GymProject } from '../types'

const props = defineProps<{
  project: GymProject
}>()

const rogue = useRogue()

const name = ref('')
const isPublic = ref(false)
const saving = ref(false)
const notice = ref<'saved' | 'save-error' | 'clipboard' | null>(null)

const canSave = computed(() => Boolean(name.value)
  && (name.value !== props.project.name || isPublic.value !== props.project.isPublic))

const url = computed(() => `${rogue.localizedBaseURL}/zeus-builder/project/${props.project._id}`)

onMounted(() => {
  name.value = props.project.name ?? ''
  isPublic.value = Boolean(props.project.isPublic)
})

async function save() {
  saving.value = true
  try {
    await rogue.fetch(`/svc_gym/projects/${props.project._id}`, 'patch', {
      isPublic : isPublic.value,
      name     : name.value,
    })

    props.project.isPublic = isPublic.value
    props.project.name = name.value
    notice.value = 'saved'
    setTimeout(() => notice.value = null, 3000)
  } catch (e) {
    notice.value = 'save-error'
    setTimeout(() => notice.value = null, 3000)
  }
  saving.value = false
}

async function share() {
  const result = await rogue.share({ url: url.value, title: props.project.title ?? '' })
  if (result === 'clipboard') {
    notice.value = 'clipboard'
    setTimeout(() => notice.value = null, 5000)
  }
}
</script>
<style lang="scss" scoped>

.share-url-title {
  margin-top: $space-s;
}

.url {
  font-size: $size-xxs;
  user-select: all;
}

.message {
  margin-bottom: $space-s;
}

</style>
